import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';
import qs from 'qs';
import {
  type TMarkAllReadPayload,
  type TNotification,
  type TNotificationListParams,
  type TNotificationListResponse,
  type TNotificationUnreadCount,
} from './types';

const endpoint = '/me/notifications';

export const notificationApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [QueryTags.Notifications, QueryTags.NotificationUnreadCount],
  })
  .injectEndpoints({
    endpoints: builder => ({
      /** Danh sách thông báo — hỗ trợ infinite scroll: tích luỹ các trang vào cùng một cache entry */
      GetNotifications: builder.query<
        TNotificationListResponse,
        TNotificationListParams
      >({
        query: params => ({
          url: `${endpoint}?${qs.stringify(params)}`,
        }),
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          const { page: _page, ...rest } = queryArgs;
          return `${endpointName}-${JSON.stringify(rest)}`;
        },
        merge: (currentCache, newItems, { arg }) => {
          if (arg.page <= 1) {
            Object.assign(currentCache, newItems);
            return;
          }
          const existingIds = new Set(currentCache.data.map(item => item.id));
          currentCache.data.push(
            ...newItems.data.filter(item => !existingIds.has(item.id))
          );
          currentCache.total_page = newItems.total_page;
          currentCache.total_count = newItems.total_count;
          currentCache.has_next = newItems.has_next;
        },
        forceRefetch: ({ currentArg, previousArg }) =>
          currentArg?.page !== previousArg?.page,
        providesTags: [QueryTags.Notifications],
      }),

      /** Số chưa đọc (badge chuông + từng tab) — SPEC mục 4.2 */
      GetNotificationUnreadCount: builder.query<TNotificationUnreadCount, void>(
        {
          query: () => ({
            url: `${endpoint}/unread-count`,
            disableSpinner: true,
          }),
          providesTags: [QueryTags.NotificationUnreadCount],
        }
      ),

      /** Đánh dấu MỘT thông báo đã đọc — SPEC mục 4.3 */
      MarkNotificationRead: builder.mutation<TNotification | void, string>({
        query: notificationId => ({
          url: `${endpoint}/${notificationId}`,
          method: 'PATCH',
          data: { is_read: true },
        }),
        invalidatesTags: invalidatesTags([QueryTags.NotificationUnreadCount]),
      }),

      /** Đánh dấu TẤT CẢ đã đọc (theo tab) — SPEC mục 4.4 */
      MarkAllNotificationsRead: builder.mutation<
        { updated_count: number } | void,
        TMarkAllReadPayload
      >({
        query: data => ({
          url: `${endpoint}/mark-all-read`,
          method: 'POST',
          data,
        }),
        invalidatesTags: invalidatesTags([QueryTags.NotificationUnreadCount]),
      }),

      /** Xóa MỘT thông báo — SPEC mục 4.5 */
      DeleteNotification: builder.mutation<void, string>({
        query: notificationId => ({
          url: `${endpoint}/${notificationId}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.NotificationUnreadCount]),
      }),
    }),
  });

export const {
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
} = notificationApi;
